<script lang="ts" setup>
import {
  useAuth,
  onMounted,
  ref,
  useRoute,
  computed,
  watch,
  useRouter,
  useAtlas,
  useDialog
} from '#imports';
import type { Tables } from '~~/types/database.types.ts';
const { setHeaderStyle, currentUser } = useAuth();
const isMounted = ref(false); // wait till this component is mounted to ensure the teleport to #page-filter works
onMounted(() => {
  setHeaderStyle('opaque');
  isMounted.value = true;
});

const { openDialog } = useDialog()
const router = useRouter();
const route = useRoute();
const menuItems = computed(() => [
  {
    label: 'My Atlas',
    id: 'my-atlas',
    showLabel: false,
    withDivider: true,
    items: [
      {
        label: 'Saved Spots',
        icon: 'ri-map-pin-fill',
        isActive: route.path === '/atlas' || route.path.startsWith('/atlas/spots'),
        action: () => router.push('/atlas')
      },
      {
        label: 'Saved Maps',
        icon: 'ri-map-2-fill',
        isActive: route.path.startsWith('/atlas/maps'),
        action: () => router.push('/atlas/maps')
      }
    ]
  },
  {
    label: 'Supporting',
    id: 'supporting',
    showLabel: true,
    withDivider: false,
    withSlot: true,
  },
  // {
  //   label: 'Following',
  //   id: 'following',
  //   showLabel: true,
  //   withDivider: false,
  //   withSlot: true,
  // },
]);

const { atlasMarkers, hightlightSpot } = useAtlas();

const openSpot = (spot: Tables<'spots'>) => {
  if (route.path.startsWith('/atlas/maps')) {
    router.push(`/atlas/maps/${spot.map_slug.current_slug}/spots/${spot.current_slug}`);
  } else {
    router.push(`/atlas/spots/${spot.current_slug}`);
  }
}

const mapComponent = ref<HTMLDivElement | null>(null);
watch(hightlightSpot, () => {
  if (mapComponent.value) {
    mapComponent.value.centerOnSpot(hightlightSpot?.value?.id)
  }
});
</script>

<template>
  <div class="atlas" v-if="currentUser">
    <div id="nav">
      <aside class="desktop">
        <h2>My Atlas</h2>
        <p>Manage your saved spots, saved maps, and creators your following.</p>
        <MPMenu :menu-items="menuItems">
          <template #['supporting']>
            <span v-if="currentUser?.supports?.length === 0">You are not currently supporting any creators</span>
            <ul v-else>
              <li v-for="creator in currentUser?.supports" :key="creator.id">
                <NuxtLinkLocale :to="`/@${creator.profiles?.mappr_handle}`">
                  <MPImg :src="creator?.profiles?.profile_image_url || '/media/images/placeholder-user.jpg'" :width="35" :height="35" :style="{ borderRadius: '100%' }"/>
                  @{{ creator?.profiles?.mappr_handle }}
                </NuxtLinkLocale>
              </li>
            </ul>
          </template>
          <!-- <template #['following']>
          {{ currentUser.follows }}
            <span v-if="currentUser?.follows?.length === 0">You are not following any creators</span>
            <ul v-else>
              <li v-for="creator in currentUser?.follows" :key="creator.id">
                <NuxtLinkLocale :to="`/@${creator.profiles?.mappr_handle}`">
                  <MPImg :src="creator?.profiles?.profile_image_url || '/media/images/placeholder-user.jpg'" :width="35" :height="35" :style="{ borderRadius: '100%' }"/>
                  @{{ creator?.profiles?.mappr_handle }}
                </NuxtLinkLocale>
              </li>
            </ul>
          </template> -->
        </MPMenu>
      </aside>
      <aside class="mobile">
        <ul>
          <li v-for="item in menuItems[0].items" :key="item.id">
            <a
              :label="item.label"
              :class="[{ active: item.isActive }]"
              @click="item.action"
            >
              <i :class="item.icon"/>
              {{ item.label }}
            </a>
          </li>
        </ul>
      </aside>
    </div>
    <div id="map">
      <ClientOnly>
        <MPMap
          ref="mapComponent"
          :locations="atlasMarkers"
          @marker-click="openSpot($event)"
        />
      </ClientOnly>
    </div>
    <div id="page-filters"/>
    <div id="list">
      <hr class="pull-handle" />
      <NuxtPage v-if="isMounted"/>
    </div>
  </div>
  <div v-if="!currentUser">
    <div class="atlas-page">
        <section class="masthead">
          <div class="container">
            <h1>Start building your personal <span>Atlas</span></h1>
            <p class="subtitle">Save your favorite spots, maps and creators. Access them anytime, anywhere.</p>
            <MPButton
              label="Start your atlas"
              version="success"
              size="large"
              rounded
              @click="openDialog('MPAuth', { startOn: 'signup' })"
            />
          </div>
          <div>
            <MPImg src="/images/map-tags-icons.png" />
          </div>
        </section>
        <section>
          <div>
            <MPSectionHeader
              :center-title="false"
              subtitle="Save what you've discovered"
              title="Save your favorite Spots and maps"
              summary="Discover new places, save your favorite spots, and explore the world with your personalized Atlas."
            />
          </div>
          <div>
            <MPImg src="/images/card-world-stack.png" />
          </div>
        </section>
        <section>
          <div>
            <MPSectionHeader
              :center-title="false"
              subtitle="Creator Curated"
              title="Discover our Creator Recommendations"
              summary="Get the insider's perspective on the best places to visit across the world"
            />
          </div>
          <div>
            <MPImg src="/images/card-circle-graphic.png" />
          </div>
        </section>
        <section>
          <div>
            <MPSectionHeader
              :center-title="false"
              subtitle="Take it on the road"
              title="Save & track down hidden Gems"
              summary="Access your saved spots and maps anytime, anywhere."
            />
          </div>
          <div>
            <MPImg src="/images/person-spots-onroad.png" />
          </div>
        </section>
        <section class="faq-section">
          <div>
            <MPSectionHeader
              :center-title="false"
              subtitle="FAQ"
              title="Frequently Asked Questions"
              summary="Find out how it works and answers to frequently asked questions."
            />
          </div>

          <MPAccordion
            :items="[
              {
                id: '1',
                header: 'What is Mappr?',
                content:
                  'Mappr is a way to support your favorite creators and have access to their knowledge of the world.',
              },
              {
                id: '2',
                header: 'Can I Save My own Spots',
                content:
                  'Currently you can only save spots that have been uploaded by Creators.',
              },
              {
                id: '3',
                header: 'How much does it cost',
                content:
                  'The Atlas is free for all Users, and we plan to keep it that way.',
              },
            ]"
            class="faq-list"
          />
        </section>
      </div>
  </div>
</template>

<style lang="postcss" scoped>
@import 'assets/css/typography.pcss';
@import 'assets/css/breakpoints.pcss';
@import 'assets/css/scrollbar.pcss';
@import 'assets/css/layouts.pcss';

/* base grid layout */
.atlas {
  display: grid;
  width: 100vw;
  min-height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  grid-template-areas:
    "map"
    "list";
  grid-template-rows: 80vh 1fr;
  @mixin screen md {
    position: relative;
    min-height: auto;
    height: calc(100vh - 7.6rem);
    grid-template-areas:
      "nav filters map"
      "nav list map";
    grid-template-columns: 18rem 25rem 1fr;
    grid-template-rows: 0 100%;
  }
}

#map {
  grid-area: map;
}

#page-filters {
  z-index: 1000;
}

#nav {
  grid-area: nav;
  z-index: 1000;
  aside.desktop {
    display: none;
    @mixin screen md {
      display: block;
    }
  }
  aside.mobile {
    display: flex;
    @mixin screen md {
      display: none;
    }
  }
}

#list {
  grid-area: list;
  z-index: 1;
  background: white;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  @mixin screen md {
    box-shadow: none;
  }
}


/*======
Map
======*/
#map {
  background-color: #5acfe4;
  position: fixed;
  width: 100%;
  height: 80vh;
  @mixin screen md {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 100%;
  }
}
/*======
Filters
======*/
#page-filters {
  position: fixed;
  top: 4.75rem;
  right: 1rem;
  height: fit-content;
  width: calc(100% - 2rem);
  z-index: 8100;
  @mixin screen md {
    width: 100%;
    position: relative;
    max-width: auto;
    top: 0;
    left: 0;
    background-color: white;
  }
}

/*======
Nav
======*/
#nav {
  overflow-x: scroll;
  position: fixed;
  top: 0rem;
  left: 0;
  padding-bottom: 3rem;
  width: 100%;
  @mixin scrollbar-reset;
  background: linear-gradient(180deg, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0.00) 100%);
  @mixin screen md {
    background-color: var(--color-slate-100);
    position: relative;
    top: 0;
    padding-left: 2vw;
    padding-right: 1rem;
  }
}

/* #nav aside.mobile ul {
  padding: 0.5rem 1rem 0.5rem;
  margin: 0;
  list-style: none;
  display: flex;
  gap: 0.75rem;
  overflow-y: scroll;
  @mixin scrollbar-reset;
} */

#nav aside.mobile ul {
  list-style: none;
  padding: 0 0 0 3.75rem;
  margin: 0;
  display: inline-flex;
  overflow-x: scroll;
  @mixin scrollbar-reset;
  @mixin screen md {
    padding: 1rem 0 0;
  }
}

#nav aside.mobile ul li a {
  display: block;
  padding: 1.15rem 0.65rem;
  text-decoration: none;
  color: var(--color-slate-500);
  transition: color 0.2s ease-in-out;
  @mixin screen md {
    padding: 0 1rem;
  }
}

#nav aside.mobile ul li a i {
  font-size: 1.25rem;
}

#nav aside.mobile ul li a.active {
  color: var(--color-slate-700);
  font-weight: 600;
  transition: color 0.2s ease-in-out;
}

#nav aside.desktop h2 {
  font-size: 1.2rem;
  font-weight: medium;
  color: var(--color-slate-600);
  margin: 2rem 0 0.5rem;
}

#nav aside.desktop p {
  font-size: 0.9rem;
  margin: 0.5rem 0 1.25rem;
  color: var(--color-slate-500);
}

#nav aside.desktop span {
  display: flex;
  line-height: 1.2;
  font-size: 0.9rem;
  margin: 0.5rem 0.5rem 1.25rem;
  color: var(--color-slate-500);
}

/*======
List
======*/

.pull-handle {
  display: block;
  @mixin screen md {
    display: none;
  }
}
#list {
  overflow-y: scroll;
  /* border-radius: 1.5rem; */
  position: relative;
  top: -3rem;
  padding-top: 1rem;
  z-index: 1000;
  @mixin scrollbar-reset;
  &:deep(> div) {
    max-width: 40rem;
    margin: 1rem auto;
  }
  @mixin screen md {
    padding-top: 0;
    top: 0;
  }
}

.atlas-page {
  @mixin layout-wrapper-wide;
  section:deep(h1) {
    max-width: 30rem;
    margin: 0 auto;
  }
  section {
    margin: 0 1rem;
    padding: 2rem 0;
    display: flex;
    flex-direction: column;
    flex-direction: column-reverse;
    gap: 2rem;
    align-items: center;
    img {
      width: 70%;
      max-width: 35rem;
      margin: 0 auto;
    }
    text-align: center;
  }
  .masthead {
    @mixin layout-wrapper-wide;
    margin: 0 1rem 4rem;
    align-items: left;
    text-align: left;
    img {
      display: none;
      max-width: 45rem;
    }
    h1 {
      @mixin typeface-title;
      margin: 0 0 1rem;
      padding-right:4vw;
      max-width: 100vw;
      span {
        color: var(--color-green-400);
      }
    }
    p {
      @mixin typeface-summary;
      color: var(--color-slate-500);
      max-width: 28rem;
      margin-bottom: 1.75rem;
      padding-right: 2rem;
    }
  }
  .faq-section {
    flex-direction: column;
    padding: 0 1rem;
  }
  @mixin screen lg {
    section {
      max-width: 80%;
      text-align: left;
      padding: 4rem 0;
      display: flex;
      flex-direction: row;
      gap: 4rem;
      margin: 0 auto;
      align-items: center;
      justify-content: space-between;

      img {
        width: 100%;
        min-width: 10rem;
        margin: 0 auto;
      }

      h1 {
        max-width: 45rem;
      }
    }
    section:nth-child(even) {
      flex-direction: row-reverse;
    }
    .masthead {
      @mixin layout-wrapper-wide;
      margin: 8rem 2rem 6rem;
      grid-template-columns: 1fr 1fr;
      padding-right: 0;
      img {
        display: block;
      }
      h1 {
        @mixin typeface-huge;
        max-width: 50rem;
      }
      p {
        max-width: 28rem;
      }
    }
  }
  .faq-section {
    text-align: center;
    margin: 0 auto 4rem;
    > * {
      max-width: 40rem;
    }
  }
  .faq-section:deep(h1) {
    max-width: 25rem;
    margin: 0 auto;
  }
}
</style>
